import { useState, useCallback, useRef, useEffect } from "react";

type Columns = { key: string; value: any }[];

export function useDynamicForm<T extends Record<string, any>>(initialValues: T) {
  const [formData, setFormData] = useState<T>(initialValues || {} as T);

  // Use a Ref to always have access to the latest state in closures
  const formDataRef = useRef<T>(formData);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const handleChange = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFormData((prev) => {
      if (prev[key] === value) return prev;
      const next = { ...prev, [key]: value };
      formDataRef.current = next; // Update Ref immediately for synchronous access
      return next;
    });
  }, []);

  const toColumns = useCallback((): Columns => {
    // Read from Ref to avoid stale closure issues during rapid updates/syncs
    return Object.entries(formDataRef.current).map(([key, value]) => {
      if (value instanceof Date) {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        const hours = String(value.getHours()).padStart(2, '0');
        const minutes = String(value.getMinutes()).padStart(2, '0');
        const seconds = String(value.getSeconds()).padStart(2, '0');

        const backendFormat = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        return { key, value: backendFormat };
      }
      if (value === null || value === undefined) {
        return { key, value: "" };
      }
      return { key, value: String(value) };
    });
  }, []);

  return {
    formData,
    handleChange,
    toColumns,
    setFormData,
  };
}
